import {
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   NgZone,
   OnChanges,
   OnDestroy,
   Output,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { formatSecondsAsString } from 'src/app/pipes/format-call-details-seconds/format-detail-seconds.pipe';
import { HangUpCallsPrivileges } from '../../active-calls/models/hang-up-calls.privileges';
import { QueueHistoryRegisterOptions } from '../models/queue-history-register-options';
import { QueueHistoryRegisterListItem } from '../models/queue-history-register-results.model';
import { selectQuickFilters } from '../state/queue-history-register.reducer';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-queue-history-register-view',
   templateUrl: './queue-history-register-view.component.html',
   standalone: false,
})
export class QueueHistoryRegisterViewComponent
   extends EntityRegisterView<QueueHistoryRegisterListItem, QueueHistoryRegisterOptions, OrderingOption>
   implements OnChanges, OnDestroy
{
   @Input() isSystemUser: boolean;

   @Input() showAccountFilters = false;

   @Input() showQueueColumn = false;

   @Input() isQueueFilterSortable = false;

   @Input() hangupActiveCallsPrivileges: HangUpCallsPrivileges;

   @Input() set queueExtensions(value: ExtensionOption[]) {
      this._queueExtensions = value ?? [];
   }

   get queueExtensions(): ExtensionOption[] {
      return this._queueExtensions;
   }

   @Output() onClearCall = new EventEmitter<QueueHistoryRegisterListItem>();

   @Output() onOpenCallDetails = new EventEmitter<QueueHistoryRegisterListItem>();

   @Output() onEmptyExitRoute = new EventEmitter<QueueHistoryRegisterListItem>();

   protected selectQuickFilters(): (state: object) => QueueHistoryRegisterOptions | null {
      return selectQuickFilters;
   }
   private waitTimeRefreshInterval: ReturnType<typeof setInterval> | null = null;

   private waitTimeRefreshNow = new Date();

   private _queueExtensions: ExtensionOption[] = [];

   private queueOrderByQueueHistoryId = new Map<number, number>();

   constructor(public store: Store, private cdRef: ChangeDetectorRef, private ngZone: NgZone) {
      super(store);
      this.startMinuteTimer();
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['dataSource']) {
         this.rebuildQueueOrder();
      }
   }

   getRouterLink(id: number): string {
      return '';
   }

   getQueueOrder(queueHistory: QueueHistoryRegisterListItem): number | null {
      return this.queueOrderByQueueHistoryId.get(queueHistory.queueHistoryId) ?? null;
   }

   protected override onQuickFiltersInit(quickFilters: QueueHistoryRegisterOptions): void {
      if (quickFilters) {
         this.registerOptions = quickFilters;
      }
   }

   ngOnDestroy() {
      this.stopMinuteTimer();
   }

   onClearClick(queueHistory: QueueHistoryRegisterListItem) {
      this.onClearCall.emit(queueHistory);
   }

   canOpenCallDetails(queueHistory: QueueHistoryRegisterListItem): boolean {
      return !!queueHistory.callDetailId;
   }

   getQueueDisplay(queueHistory: QueueHistoryRegisterListItem): string {
      const directQueue = queueHistory.queueExtension;
      if (directQueue) {
         return this.getQueueLabel(
            directQueue.number ? String(directQueue.number) : null,
            this.getQueueDisplayName(directQueue.shortName, directQueue.name)
         );
      }

      const queueExtensionId = queueHistory.queueExtensionId ?? null;
      if (!queueExtensionId) {
         return '';
      }

      const selectedQueue = this.queueExtensions.find((queueExtension) => {
         return queueExtension.extensionId === queueExtensionId;
      });

      if (!selectedQueue) {
         return String(queueExtensionId);
      }

      return this.getQueueLabel(
         selectedQueue.number != null ? String(selectedQueue.number) : null,
         this.getQueueDisplayName(selectedQueue.shortName, selectedQueue.name)
      );
   }

   private getQueueDisplayName(shortName: string | null | undefined, name: string | null | undefined): string | null {
      return shortName?.trim() || name?.trim() || null;
   }

   getQueueLabel(number: string | null, name: string | null): string {
      if (number && name) {
         return `${number} - ${name}`;
      }

      if (name) {
         return name;
      }

      if (number) {
         return number;
      }

      return '';
   }

   getWaitTimeDisplay(queueHistory: QueueHistoryRegisterListItem): string {
      const enqueuedDateTime = this.toDate(queueHistory.enqueuedDateTime);
      if (!enqueuedDateTime) {
         return '<1m';
      }

      const endDateTime = this.toDate(queueHistory.answeredDateTime);
      const endTime = endDateTime?.getTime() ?? this.waitTimeRefreshNow.getTime();
      const elapsedSeconds = Math.max(0, Math.floor((endTime - enqueuedDateTime.getTime()) / 1000));

      return formatSecondsAsString(elapsedSeconds);
   }

   onOpenCallDetailClick(queueHistory: QueueHistoryRegisterListItem) {
      if (!this.canOpenCallDetails(queueHistory)) {
         return;
      }

      this.onOpenCallDetails.emit(queueHistory);
   }

   onEmptyExitRouteClick(queueHistory: QueueHistoryRegisterListItem) {
      this.onEmptyExitRoute.emit(queueHistory);
   }

   private startMinuteTimer() {
      this.waitTimeRefreshInterval = setInterval(() => {
         this.ngZone.run(() => {
            this.waitTimeRefreshNow = new Date();
            this.cdRef.detectChanges();
         });
      }, 1000);
   }

   private stopMinuteTimer() {
      if (this.waitTimeRefreshInterval) {
         clearInterval(this.waitTimeRefreshInterval);
      }
      this.waitTimeRefreshInterval = null;
   }

   private toDate(value: Date | string | null | undefined): Date | null {
      if (!value) {
         return null;
      }

      if (value instanceof Date) {
         return Number.isFinite(value.getTime()) ? value : null;
      }

      const parsedDate = new Date(value);
      return Number.isFinite(parsedDate.getTime()) ? parsedDate : null;
   }

   private rebuildQueueOrder(): void {
      const queueHistoryByEnqueuedDateTime = [...(this.dataSource ?? [])].sort((a, b) => {
         const aEnqueuedDateTime = this.toDate(a.enqueuedDateTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
         const bEnqueuedDateTime = this.toDate(b.enqueuedDateTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;

         if (aEnqueuedDateTime !== bEnqueuedDateTime) {
            return aEnqueuedDateTime - bEnqueuedDateTime;
         }

         return (a.queueHistoryId ?? 0) - (b.queueHistoryId ?? 0);
      });

      this.queueOrderByQueueHistoryId = new Map(
         queueHistoryByEnqueuedDateTime.map((queueHistory, index) => [queueHistory.queueHistoryId, index + 1])
      );
   }
}
